// src/components/HomeOutlet.jsx
import React from "react";
import styled from "styled-components";
import { Outlet } from "react-router-dom";
import HomeNavbar from "../components/HomeNavbar";
import Draggable from "react-draggable";
import { Rnd } from "react-rnd";
import svgPanZoom from "svg-pan-zoom";

const LayoutContainer = styled.div`
  padding-top: 100px; /* Matches fixed navbar height */
`;

/* ---------------- CAD SVG Viewer with pan/zoom ---------------- */
/**
 * Renders an SVG fetched from the reader service, wraps it in
 * svg-pan-zoom so the user can wheel-zoom, click-drag to pan,
 * and use the built-in +/-/reset controls (bottom-right of the SVG).
 *
 * `selectedLayer` — the layer name to show alone, or null to show all.
 *   The backend groups items by layer inside <g data-layer="X">.
 *   Changing `selectedLayer` toggles group visibility WITHOUT
 *   re-fetching the SVG, so the pan/zoom view is preserved.
 */
function CadSvgViewer({ svgUrl, selectedLayer }) {
  const containerRef = React.useRef(null);
  const panZoomRef = React.useRef(null);

  // Load / reload the SVG whenever the URL changes.
  React.useEffect(() => {
    let cancelled = false;
    if (panZoomRef.current) {
      try {
        panZoomRef.current.destroy();
      } catch (e) {}
      panZoomRef.current = null;
    }
    if (!svgUrl || !containerRef.current) return;

    (async () => {
      try {
        const res = await fetch(svgUrl);
        const text = await res.text();
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = text;
        const svgEl = containerRef.current.querySelector("svg");
        if (!svgEl) return;

        svgEl.setAttribute("width", "100%");
        svgEl.setAttribute("height", "100%");
        svgEl.style.display = "block";

        panZoomRef.current = svgPanZoom(svgEl, {
          zoomEnabled: true,
          controlIconsEnabled: true,
          fit: true,
          center: true,
          minZoom: 0.05,
          maxZoom: 50,
          zoomScaleSensitivity: 0.3,
          dblClickZoomEnabled: true,
          mouseWheelZoomEnabled: true,
          panEnabled: true,
        });
      } catch (err) {
        console.error("CadSvgViewer load failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      if (panZoomRef.current) {
        try {
          panZoomRef.current.destroy();
        } catch (e) {}
        panZoomRef.current = null;
      }
    };
  }, [svgUrl]);

  // Toggle layer visibility (no re-fetch — preserves pan/zoom).
  // Each layer's <g> starts with a <title>{layerName}</title> child,
  // so we read the layer name from there.
  React.useEffect(() => {
    if (!containerRef.current) return;
    const groups = containerRef.current.querySelectorAll("g");
    groups.forEach((g) => {
      const titleEl = g.querySelector(":scope > title");
      if (!titleEl) return; // not a layer group
      const layer = titleEl.textContent || "";
      if (!selectedLayer || layer === selectedLayer) {
        g.style.display = "";
      } else {
        g.style.display = "none";
      }
    });
  }, [selectedLayer]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        cursor: "grab",
        background: "#fafafa",
      }}
    />
  );
}

/* ---------------- Quantities panel defaults ---------------- */
// Mirrors quantities_estimation.py's DEFAULTS dict — kept in sync
// manually since the frontend can't import the Python module.
const QUANTITIES_DEFAULTS = {
  height: 3.0,
  brick_length: 0.4,
  brick_height: 0.2,
  mortar: 0.0,
  door_height: 2.2,
  window_height: 1.2,
  waste: 5.0,
};

export default function HomeOutlet() {
  const [cadInfo, setCadInfo] = React.useState(null);
  const [showCadPanel, setShowCadPanel] = React.useState(false);
  const [market, setMarket] = React.useState("tadawul");
  // Which single layer is visible in the CAD Viewer? null = "All".
  const [selectedLayer, setSelectedLayer] = React.useState(null);
  // Which layer(s) to extract into the walls JSON/DXF? Up to 3,
  // checkbox-selected by the user.
  const [selectedLayers, setSelectedLayers] = React.useState([]);
  const [saveAsName, setSaveAsName] = React.useState("");
  const [saveMsg, setSaveMsg] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // ── Quantities panel state ──────────────────────────────────────────
  const [showQuantitiesPanel, setShowQuantitiesPanel] = React.useState(false);
  const [quantitiesFile, setQuantitiesFile] = React.useState(null); // File object
  const [quantitiesParams, setQuantitiesParams] =
    React.useState(QUANTITIES_DEFAULTS);
  const [quantitiesOwner, setQuantitiesOwner] = React.useState("");
  // Number of identical floors (1-20). 1 = single-floor report only.
  const [quantitiesFloors, setQuantitiesFloors] = React.useState(1);
  const [quantitiesResult, setQuantitiesResult] = React.useState(null); // {plan_name, english_text, arabic_text, pdf_base64}
  const [quantitiesError, setQuantitiesError] = React.useState("");
  const [quantitiesLoading, setQuantitiesLoading] = React.useState(false);
  const [quantitiesTab, setQuantitiesTab] = React.useState("en"); // 'en' | 'ar'

  const handleCadUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setShowCadPanel(false);
    setCadInfo(null);
    setSelectedLayer(null);
    setSelectedLayers([]);
    setSaveAsName("");
    setSaveMsg("");
    setSaving(false);

    try {
      const res = await fetch("https://cad-api.saudidune.com/cad/info", {
        method: "POST",
        body: formData,
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
      setTimeout(() => {
        setCadInfo(data);
        setShowCadPanel(true);
      }, 0);
    } catch (err) {
      setCadInfo({ detail: err.message });
      setShowCadPanel(true);
    }
  };

  // Trigger a browser download of a Blob under the given filename.
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Toggle a layer's checkbox — enforces a max of 3 selected at once.
  const toggleWallsLayer = (name) => {
    setSelectedLayers((prev) => {
      if (prev.includes(name)) {
        return prev.filter((n) => n !== name);
      }
      if (prev.length >= 3) return prev; // already at the cap
      return [...prev, name];
    });
  };

  const handleSaveWalls = async () => {
    if (!cadInfo?.dxf_id) {
      setSaveMsg("No cached DXF — re-upload the file.");
      return;
    }
    if (selectedLayers.length === 0) {
      setSaveMsg("Pick at least one layer (up to 3).");
      return;
    }
    const name = (saveAsName || "walls_export").trim();
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await fetch("https://cad-api.saudidune.com/cad/save-walls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dxf_id: cadInfo.dxf_id,
          layers: selectedLayers,
          save_as: name,
        }),
      });
      const data = await res.json();
      if (data.detail) {
        setSaveMsg(`Error: ${data.detail}`);
        return;
      }
      // Decode DXF from base64 and download both files.
      const b64 = data.dxf_base64 || "";
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      downloadBlob(new Blob([bytes], { type: "image/vnd.dxf" }), `${name}.dxf`);
      downloadBlob(
        new Blob([data.json_text || "{}"], { type: "application/json" }),
        `${name}.json`
      );
      setSaveMsg(`Saved ${name}.dxf and ${name}.json`);
    } catch (err) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Quantities panel handlers ───────────────────────────────────────
  const openQuantitiesPanel = () => {
    setQuantitiesFile(null);
    setQuantitiesParams(QUANTITIES_DEFAULTS);
    setQuantitiesOwner("");
    setQuantitiesFloors(1);
    setQuantitiesResult(null);
    setQuantitiesError("");
    setQuantitiesLoading(false);
    setQuantitiesTab("en");
    setShowQuantitiesPanel(true);
  };

  const handleQuantitiesFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setQuantitiesFile(file);
  };

  const setParam = (key, value) => {
    setQuantitiesParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleCalculateQuantities = async () => {
    if (!quantitiesFile) {
      setQuantitiesError("Choose a floorplan JSON file first.");
      return;
    }
    setQuantitiesLoading(true);
    setQuantitiesError("");
    setQuantitiesResult(null);
    try {
      const formData = new FormData();
      formData.append("file", quantitiesFile);
      formData.append("height", quantitiesParams.height);
      formData.append("brick_length", quantitiesParams.brick_length);
      formData.append("brick_height", quantitiesParams.brick_height);
      formData.append("mortar", quantitiesParams.mortar);
      formData.append("door_height", quantitiesParams.door_height);
      formData.append("window_height", quantitiesParams.window_height);
      formData.append("waste", quantitiesParams.waste);
      formData.append("owner", quantitiesOwner);
      formData.append("floors", quantitiesFloors);

      const res = await fetch(
        "https://cad-api.saudidune.com/quantities/calculate",
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await res.json();
      if (data.detail) {
        setQuantitiesError(data.detail);
        return;
      }
      setQuantitiesResult(data);
      setQuantitiesTab("en");
    } catch (err) {
      setQuantitiesError(err.message);
    } finally {
      setQuantitiesLoading(false);
    }
  };

  const handleDownloadEnglishPdf = () => {
    if (!quantitiesResult?.pdf_base64) return;
    const bytes = Uint8Array.from(atob(quantitiesResult.pdf_base64), (c) =>
      c.charCodeAt(0)
    );
    const name = (quantitiesResult.plan_name || "quantities").replace(
      /\s+/g,
      "_"
    );
    downloadBlob(
      new Blob([bytes], { type: "application/pdf" }),
      `${name}_quantities.pdf`
    );
  };

  const handleDownloadArabicTxt = () => {
    if (!quantitiesResult?.arabic_text) return;
    // UTF-8 BOM prefix — same reasoning as the Python script: makes
    // Notepad reliably auto-detect UTF-8 across Windows versions.
    const bom = "\uFEFF";
    const name = (quantitiesResult.plan_name || "quantities").replace(
      /\s+/g,
      "_"
    );
    downloadBlob(
      new Blob([bom + quantitiesResult.arabic_text], {
        type: "text/plain;charset=utf-8",
      }),
      `${name}_quantities_ar.txt`
    );
  };

  return (
    <LayoutContainer>
      <HomeNavbar
        market={market}
        setMarket={setMarket}
        onCadUpload={handleCadUpload}
        onOpenQuantities={openQuantitiesPanel}
        greeting
      />

      {/* Hidden file input triggered by the navbar's AutoCAD link. */}
      <input
        type="file"
        id="cad-upload-input"
        accept=".dxf,.dwg"
        style={{ display: "none" }}
        onChange={handleCadUpload}
      />

      {/* Draggable CAD Viewer panel */}
      {showCadPanel && cadInfo && (
        <Rnd
          default={{ x: 60, y: 120, width: 1000, height: 720 }}
          minWidth={480}
          minHeight={360}
          bounds="window"
          dragHandleClassName="cad-viewer-drag-handle"
          style={{ zIndex: 9999 }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 8,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              className="cad-viewer-drag-handle"
              style={{
                background: "#222",
                color: "white",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "move",
              }}
            >
              <div style={{ fontWeight: 600 }}>CAD Viewer</div>
              <button
                onClick={() => setShowCadPanel(false)}
                style={{
                  background: "#c0392b",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: 16,
                background: "white",
              }}
            >
              {cadInfo.detail && (
                <div style={{ color: "#c0392b" }}>{cadInfo.detail}</div>
              )}

              {cadInfo.layers && (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Entity Count:</strong> {cadInfo.entity_count}
                  </div>

                  {/* Layer picker — checkboxes, up to 3 layers, laid
                      out horizontally (wrap) so the drawing preview
                      below keeps most of the panel's height. */}
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 12,
                      background: "#eef7f0",
                      border: "1px solid #bfdac9",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: 6 }}>
                      Save layers as DXF + JSON
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#555", marginBottom: 10 }}
                    >
                      Pick 1–3 layers (e.g. walls + doors + windows + text).
                      Each stays on its own named layer in the DXF — raw
                      pass-through, no merging, no cleanup. The JSON combines
                      all selected layers' LINEs into one walls list
                      (double-line pairs merged into single centerlines) and all
                      TEXT/MTEXT into a texts list (room labels etc.).
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 10,
                      }}
                    >
                      {(cadInfo.layers || []).map((name) => {
                        const checked = selectedLayers.includes(name);
                        const disabled = !checked && selectedLayers.length >= 3;
                        return (
                          <label
                            key={name}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 10px",
                              background: checked ? "#e8f5e9" : "white",
                              border: "1px solid #cfe0d3",
                              borderRadius: 999,
                              fontSize: 13,
                              cursor: disabled ? "not-allowed" : "pointer",
                              opacity: disabled ? 0.5 : 1,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleWallsLayer(name)}
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div
                      style={{ fontSize: 11, color: "#777", marginBottom: 8 }}
                    >
                      Selected: {selectedLayers.length}/3
                      {selectedLayers.length > 0 &&
                        ` — ${selectedLayers.join(", ")}`}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <label style={{ fontSize: 13 }}>Save as:</label>
                      <input
                        type="text"
                        value={saveAsName}
                        onChange={(e) => setSaveAsName(e.target.value)}
                        placeholder="e.g. tower_walls"
                        style={{
                          flex: 1,
                          padding: "4px 8px",
                          border: "1px solid #cfe0d3",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      />
                      <span style={{ fontSize: 12, color: "#666" }}>
                        .dxf / .json
                      </span>
                      <button
                        onClick={handleSaveWalls}
                        disabled={saving || selectedLayers.length === 0}
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                          background:
                            saving || selectedLayers.length === 0
                              ? "#95a5a6"
                              : "#2e8b57",
                          color: "white",
                          border: "none",
                          borderRadius: 4,
                          cursor:
                            saving || selectedLayers.length === 0
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {saving ? "Saving…" : "Download walls"}
                      </button>
                    </div>
                    {saveMsg && (
                      <div
                        style={{
                          fontSize: 12,
                          color: saveMsg.startsWith("Error")
                            ? "#c0392b"
                            : "#2e8b57",
                        }}
                      >
                        {saveMsg}
                      </div>
                    )}
                  </div>

                  {/* Layer visibility chips (viewer only) */}
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{ fontSize: 12, color: "#555", marginBottom: 4 }}
                    >
                      Show layer in preview:
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <button
                        onClick={() => setSelectedLayer(null)}
                        style={{
                          padding: "4px 10px",
                          fontSize: 12,
                          background:
                            selectedLayer === null ? "#2e8b57" : "#fff",
                          color: selectedLayer === null ? "white" : "#333",
                          border: "1px solid #bfdac9",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        All
                      </button>
                      {(cadInfo.layers || []).map((name) => (
                        <button
                          key={name}
                          onClick={() => setSelectedLayer(name)}
                          style={{
                            padding: "4px 10px",
                            fontSize: 12,
                            background:
                              selectedLayer === name ? "#2e8b57" : "#fff",
                            color: selectedLayer === name ? "white" : "#333",
                            border: "1px solid #bfdac9",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* SVG preview */}
                  {cadInfo.svg_file && (
                    <div
                      style={{
                        width: "100%",
                        height: 500,
                        border: "1px solid #ddd",
                        borderRadius: 4,
                        background: "#fafafa",
                      }}
                    >
                      <CadSvgViewer
                        svgUrl={`https://cad-api.saudidune.com/svg/${cadInfo.svg_file}`}
                        selectedLayer={selectedLayer}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Rnd>
      )}

      {/* Draggable Quantities panel */}
      {showQuantitiesPanel && (
        <Rnd
          default={{ x: 100, y: 120, width: 900, height: 680 }}
          minWidth={520}
          minHeight={400}
          bounds="window"
          dragHandleClassName="quantities-drag-handle"
          style={{ zIndex: 9999 }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 8,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              className="quantities-drag-handle"
              style={{
                background: "#222",
                color: "white",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "move",
              }}
            >
              <div style={{ fontWeight: 600 }}>Quantities Estimation</div>
              <button
                onClick={() => setShowQuantitiesPanel(false)}
                style={{
                  background: "#c0392b",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: 16,
                background: "white",
              }}
            >
              {/* ── Setup form (file + params + owner) ── */}
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#eef7f0",
                  border: "1px solid #bfdac9",
                  borderRadius: 8,
                }}
              >
                <div style={{ fontWeight: "bold", marginBottom: 10 }}>
                  Floorplan JSON
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleQuantitiesFileChange}
                    style={{ fontSize: 13 }}
                  />
                  {quantitiesFile && (
                    <span style={{ fontSize: 12, color: "#2e8b57" }}>
                      {quantitiesFile.name}
                    </span>
                  )}
                </div>

                <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                  Parameters
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 10,
                    marginBottom: 12,
                  }}
                >
                  {[
                    ["height", "Wall height (m)"],
                    ["brick_length", "Brick length (m)"],
                    ["brick_height", "Brick height (m)"],
                    ["mortar", "Mortar joint (m)"],
                    ["door_height", "Door height (m)"],
                    ["window_height", "Window height (m)"],
                    ["waste", "Waste (%)"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ fontSize: 12, color: "#444" }}>
                      {label}
                      <input
                        type="number"
                        step="0.01"
                        value={quantitiesParams[key]}
                        onChange={(e) =>
                          setParam(key, parseFloat(e.target.value) || 0)
                        }
                        style={{
                          display: "block",
                          width: "100%",
                          marginTop: 3,
                          padding: "4px 6px",
                          border: "1px solid #cfe0d3",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      />
                    </label>
                  ))}
                </div>

                <label style={{ fontSize: 12, color: "#444" }}>
                  Owner (optional — report title becomes "&lt;owner&gt;
                  building")
                  <input
                    type="text"
                    value={quantitiesOwner}
                    onChange={(e) => setQuantitiesOwner(e.target.value)}
                    placeholder="e.g. abdullah"
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: 3,
                      padding: "4px 8px",
                      border: "1px solid #cfe0d3",
                      borderRadius: 4,
                      fontSize: 13,
                      marginBottom: 12,
                    }}
                  />
                </label>

                <label style={{ fontSize: 12, color: "#444" }}>
                  Number of floors (tower — 1 = single floor only)
                  <select
                    value={quantitiesFloors}
                    onChange={(e) =>
                      setQuantitiesFloors(parseInt(e.target.value, 10))
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: 3,
                      padding: "4px 8px",
                      border: "1px solid #cfe0d3",
                      borderRadius: 4,
                      fontSize: 13,
                      marginBottom: 12,
                      background: "white",
                    }}
                  >
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  onClick={handleCalculateQuantities}
                  disabled={quantitiesLoading || !quantitiesFile}
                  style={{
                    padding: "8px 18px",
                    fontSize: 13,
                    fontWeight: 600,
                    background:
                      quantitiesLoading || !quantitiesFile
                        ? "#95a5a6"
                        : "#2e8b57",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor:
                      quantitiesLoading || !quantitiesFile
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {quantitiesLoading ? "Calculating…" : "Calculate"}
                </button>

                {quantitiesError && (
                  <div
                    style={{ marginTop: 10, fontSize: 12, color: "#c0392b" }}
                  >
                    Error: {quantitiesError}
                  </div>
                )}
              </div>

              {/* ── Results: 2 tabs ── */}
              {quantitiesResult && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      marginBottom: 10,
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <button
                      onClick={() => setQuantitiesTab("en")}
                      style={{
                        padding: "8px 18px",
                        fontSize: 13,
                        fontWeight: 600,
                        background: "transparent",
                        color: quantitiesTab === "en" ? "#2e8b57" : "#888",
                        border: "none",
                        borderBottom:
                          quantitiesTab === "en"
                            ? "3px solid #2e8b57"
                            : "3px solid transparent",
                        cursor: "pointer",
                      }}
                    >
                      English
                    </button>
                    <button
                      onClick={() => setQuantitiesTab("ar")}
                      style={{
                        padding: "8px 18px",
                        fontSize: 13,
                        fontWeight: 600,
                        background: "transparent",
                        color: quantitiesTab === "ar" ? "#2e8b57" : "#888",
                        border: "none",
                        borderBottom:
                          quantitiesTab === "ar"
                            ? "3px solid #2e8b57"
                            : "3px solid transparent",
                        cursor: "pointer",
                      }}
                    >
                      العربية
                    </button>
                  </div>

                  {quantitiesTab === "en" && (
                    <div>
                      <button
                        onClick={handleDownloadEnglishPdf}
                        style={{
                          marginBottom: 10,
                          padding: "6px 14px",
                          fontSize: 13,
                          background: "#2e8b57",
                          color: "white",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        Download PDF
                      </button>
                      <pre
                        style={{
                          margin: 0,
                          padding: 12,
                          background: "#fafafa",
                          border: "1px solid #ddd",
                          borderRadius: 4,
                          fontSize: 12,
                          lineHeight: 1.5,
                          fontFamily: "'Courier New', Consolas, monospace",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 420,
                          overflow: "auto",
                        }}
                      >
                        {quantitiesResult.english_text}
                      </pre>
                    </div>
                  )}

                  {quantitiesTab === "ar" && (
                    <div>
                      <button
                        onClick={handleDownloadArabicTxt}
                        style={{
                          marginBottom: 10,
                          padding: "6px 14px",
                          fontSize: 13,
                          background: "#2e8b57",
                          color: "white",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        Download .txt (Notepad)
                      </button>
                      <pre
                        dir="rtl"
                        style={{
                          margin: 0,
                          padding: 12,
                          background: "#fafafa",
                          border: "1px solid #ddd",
                          borderRadius: 4,
                          fontSize: 13,
                          lineHeight: 1.8,
                          fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          textAlign: "right",
                          maxHeight: 420,
                          overflow: "auto",
                        }}
                      >
                        {quantitiesResult.arabic_text}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Rnd>
      )}

      <Outlet />
    </LayoutContainer>
  );
}
